import React, { useState } from 'react';
import { isABD, isNonProcured, STAGES, STAGE_KEYS, SCAN_STAGES, STATUS_ZONE, ZONE_COLORS, MANUALLY_SELECTABLE_STATUSES, STATUSES, estimatedShipDate, threadStateFor, didThisShipQueue } from '../utils';
import ItemDocuments from './ItemDocuments';
import { Dropdown } from './ui';
import { RING_HEX } from './Thread';
import { useAppContext } from './AppContext';
import { callClaude, dateStr } from './helpers';

// Stage → its own date field (the timeline's registry, tracker-side).
var STAGE_DATE_FIELDS = {
  'Client Approved': 'clientApprovedDate',
  'Deposit Paid':    'depositPaidDate',
  'Ordered':         'orderPlacedDate',
  'Shipped':         'shippedDate',
  'At Receiver':     'receivedDate',
  'Delivered':       'deliveredDate',
  'Installed':       'installDate',
};
var PHYSICAL_STAGE_OPTIONS = [
  { value: 'Ordered', dot: true, sub: 'vendor paid / order confirmed' },
  { value: 'Shipped', dot: true, sub: 'carrier has it' },
  { value: 'At Receiver', dot: true, sub: 'receiver / workroom / fabricator' },
  { value: 'Delivered', dot: true, sub: 'on site' },
  { value: 'Installed', dot: true, sub: 'installation complete' },
  { value: 'Complete', dot: true, sub: 'installed + ledgers settled' },
];
function fmtShort(iso) {
  var s = String(iso || '');
  var m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return s; // legacy locale-format stamps display verbatim
  try {
    var d = new Date(parseInt(m[1],10), parseInt(m[2],10)-1, parseInt(m[3],10));
    return isNaN(d.getTime()) ? s : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
  } catch (e) { return s; }
}
var todayIsoStr = function() { return new Date().toISOString().slice(0, 10); };

export default function TrackerView(p){
  // STAGES + STAGE_KEYS + SCAN_STAGES imported from utils.js — single
  // source of truth shared with the proposal detail and any other
  // consumer. SCAN_STAGES is derived from STAGES (entries with
  // scan: true) so it auto-stays-in-sync.

  // ── STATE ────────────────────────────────────────────────────────────────
  // Default to "" = All projects so Moskito and Ptarmigan are visible on first
  // load. User clicks a project pill at the top to filter.
  var fps=useState("");
  var fProject=fps[0];var setFProject=fps[1];
  var fss=useState("All");var fStage=fss[0];var setFStage=fss[1];
  var fzs=useState("All");var fZone=fzs[0];var setFZone=fzs[1];
  var fvs=useState("");var fVendor=fvs[0];var setFVendor=fvs[1];

  // STATUS_ZONE + ZONE_COLORS imported from utils.js — canonical definitions
  // shared with statusColor() and the rest of the app. Local aliases for
  // brevity in this file's render code.
  var ZONE_OF = STATUS_ZONE;
  var ZONE_COLOR = ZONE_COLORS;
  // Stage-change-with-date (Cimon 2026-07-28: "if you change the status —
  // received for example — can you enter date?"). Picking a stage from
  // the row Dropdown opens an inline confirm: date prefilled to today,
  // editable, Save/Cancel. Nothing commits until Save.
  var psS=useState(null);var pendingStage=psS[0];var setPendingStage=psS[1];
  var ctx = useAppContext();
  var moneyBundle = {
    items: ctx.items || [],
    invoices: ctx.invoices || [],
    allocations: ctx.paymentAllocations || [],
    vendorPayments: ctx.vendorPayments || [],
    purchaseOrders: ctx.purchaseOrders || [],
  };
  var sqS=useState(false);var shipQueueOpen=sqS[0];var setShipQueueOpen=sqS[1];
  var scanS=useState(null);var scanItem=scanS[0];var setScanItem=scanS[1];
  var menuS=useState(null);var openMenu=menuS[0];var setOpenMenu=menuS[1];
  var scanTextS=useState("");var scanText=scanTextS[0];var setScanText=scanTextS[1];
  var scanResultS=useState(null);var scanResult=scanResultS[0];var setScanResult=scanResultS[1];
  var scanLoadS=useState(false);var scanLoading=scanLoadS[0];var setScanLoading=scanLoadS[1];
  var vs=useState('pipeline');var view=vs[0];var setView=vs[1];

  // ABD/Pre-App items came from the prior project owner — they belong to a
  // different financial track and don't belong in the procurement pipeline.
  // Hidden from the tracker entirely.
  var parents=(p.items||[]).filter(function(it){return!it.parentId&&!isABD(it);});
  var filtered=parents.filter(function(it){
    return(!fProject||it.projectLabel===fProject||it.project===fProject)&&
    (fStage!=="All"?it.status===fStage:fZone!=="All"?ZONE_OF[it.status]===fZone:true)&&
    (!fVendor||it.vendor&&it.vendor.toLowerCase().indexOf(fVendor.toLowerCase())>=0);
  });

  // ── ACTIONS ──────────────────────────────────────────────────────────────
  // Status advance with assembly-peer coupling. For stages at or before
  // Deposit Paid, all peers in the same assembly advance together --
  // they share a proposal, deposit, etc. After PO Issued, peers can
  // diverge (different vendors → different POs → different ship dates),
  // so we DON'T fan out for those later stages.
  var EARLY_STAGES_GROUP = ['Quote Received','Presented to Client','On Proposal','Client Approved','Deposit Paid'];
  function advanceTo(id,newStatus){
    var item=(p.items||[]).find(function(it){return it.id===id;});
    if(!item)return;
    var patch={status:newStatus};
    // ISO stamps (2026-07-28) — same format as the item page's timeline
    // date inputs; dateStr() is locale text and doesn't round-trip.
    var stampIso=todayIsoStr();
    if(newStatus==="Client Approved")patch.clientApprovedDate=stampIso;
    if(newStatus==="Deposit Paid")patch.depositPaidDate=stampIso;
    if(newStatus==="Shipped")patch.shippedDate=stampIso;
    if(newStatus==="At Receiver")patch.receivedDate=stampIso;
    if(newStatus==="Delivered")patch.deliveredDate=stampIso;
    if(newStatus==="Installed")patch.installDate=stampIso;

    var ids=[id];
    if(item.assemblyId && EARLY_STAGES_GROUP.indexOf(newStatus)>=0){
      (p.items||[]).forEach(function(peer){
        if(peer.assemblyId===item.assemblyId && peer.id!==id){
          ids.push(peer.id);
        }
      });
    }
    ids.forEach(function(targetId){
      var t=(p.items||[]).find(function(x){return x.id===targetId;});
      if(t)p.onUpdate(targetId,patch); // patch-only — see applyStageWithDate
    });
    setScanItem(null);setScanResult(null);setScanText("");
  }

  // Commit a stage picked from the row Dropdown, with the user's date.
  // Same write as the item page's timeline: status + that stage's own
  // date field (Complete has no date). Single item — physical stages
  // never fan out to assembly peers (each peer ships on its own clock).
  function applyStageWithDate(itemId, stage, date) {
    var t = (p.items || []).find(function(x) { return x.id === itemId; });
    if (!t) return;
    var patch = { status: stage };
    var df = STAGE_DATE_FIELDS[stage];
    if (df && date) patch[df] = date;
    // PATCH ONLY (2026-07-29): updateItem merges into the always-fresh
    // itemsRef. Passing the full render-row here could resurrect stale
    // fields (e.g. wipe a just-entered received date) — the 2026-07-08
    // SKU-loss bug shape.
    p.onUpdate(itemId, patch);
    setPendingStage(null);
  }

  function openScan(item){setScanItem(item);setScanResult(null);setScanText("");}

  function runScan(item){
    if(!scanText.trim())return;
    setScanLoading(true);setScanResult(null);
    var specs=item.specs||{};
    var specStr=Object.entries(specs).filter(function(e){return e[1];}).map(function(e){return e[0]+": "+e[1];}).join("\n")||"No specs on file";
    var stage=item.status;
    // The document being checked is whatever the VENDOR sent about the
    // order — their confirmation, invoice, or packing slip. The old
    // "purchase order" fallback label was wrong for the Order Confirmed
    // stage (the PO is the firm's own outbound document; comparing it
    // against the firm's own database is circular). The dead "Proposal
    // Received" branch named a status that no longer exists.
    var docType=stage==="Quote Received"?"vendor quote":"vendor document (order confirmation, invoice, or packing slip)";
    var sys="You are a verification assistant for Cimon Starr Interior Design. Compare a "+docType+" against the item specs on file. Return JSON only: {\"match\":true/false,\"flags\":[{\"field\":\"\",\"dbValue\":\"\",\"docValue\":\"\",\"issue\":\"\"}],\"summary\":\"\"}";
    var usr="Item: "+item.item+"\nDatabase specs:\n"+specStr+"\n\n"+docType.toUpperCase()+":\n"+scanText;
    callClaude(sys,usr).then(function(res){
      try{var d=JSON.parse(res.replace(/```json|```/g,"").trim());setScanResult(d);}
      catch(e){setScanResult({match:false,flags:[],summary:"Could not parse — review manually."});}
      setScanLoading(false);
    });
  }

  function exportCSV(){
    var hd=["Item","Project","Room #","Room","Vendor","Stage","Lead Time","PO","SIDEMARK","Ordered","Est. Ship","Shipped","At Receiver","Delivered","Installed"];
    var rows=filtered.map(function(it){
      return[it.productName||it.item,it.projectLabel||it.project||"",it.roomNumber||"",it.roomName||"",it.vendor||"",it.status,it.leadTime||"",it.poNumber||"",it.sidemark||"",it.orderPlacedDate||"",estimatedShipDate(it)||"",it.shippedDate||"",it.receivedDate||"",it.deliveredDate||"",it.installDate||""];
    });
    var csv=[hd].concat(rows).map(function(r){return r.map(function(c){return'"'+(c||"")+'"';}).join(",");}).join("\n");
    var a=document.createElement("a");a.href=URL.createObjectURL(new Blob([csv],{type:"text/csv"}));
    a.download="tracker-"+new Date().toISOString().slice(0,10)+".csv";a.click();
  }

  // ── STAGE COUNTS ─────────────────────────────────────────────────────────
  // Respect the project filter — empty fProject means "All projects".
  function projMatch(it){return !fProject||it.projectLabel===fProject||it.project===fProject;}
  var stageCounts={};
  STAGE_KEYS.forEach(function(k){stageCounts[k]=parents.filter(function(it){return projMatch(it)&&it.status===k;}).length;});
  var attention=parents.filter(function(it){return projMatch(it)&&SCAN_STAGES.indexOf(it.status)>=0;}).length;

  // ── SCAN MODAL ────────────────────────────────────────────────────────────
  var scanModal = null;
  if (scanItem) {
    var stage = scanItem.status;
    var nextStage = STAGES.find(function(s){return s.key===stage;});
    var nextKey = nextStage ? nextStage.next : null;
    var scanLabel = stage==='Quote Received' ? 'Paste vendor quote text' :
      'Paste vendor document text — confirmation, invoice, or packing slip';
    var flags = (scanResult&&scanResult.flags&&scanResult.flags.length>0) ? (
      <div style={{marginBottom:12}}>
        {scanResult.flags.map(function(fl,i){
          return (
            <div key={i} style={{background:'#F7F7F5',
              border:'0.5px solid #D8D7D2',borderRadius:5,
              padding:'8px 10px',marginBottom:5,fontSize:11}}>
              <div style={{fontWeight:500,color:'#C8322A',marginBottom:2}}>
                {fl.field||'Issue'}
              </div>
              <div style={{color:'#555552'}}>
                <span style={{fontWeight:500}}>Database: </span>
                {fl.dbValue||'—'}
              </div>
              <div style={{color:'#555552'}}>
                <span style={{fontWeight:500}}>Document: </span>
                {fl.docValue||'—'}
              </div>
              {fl.issue && (
                <div style={{color:'#C8322A',marginTop:2,fontSize:10}}>
                  {fl.issue}
                </div>
              )}
            </div>
          );
        })}
      </div>
    ) : null;
    var matchBanner = scanResult ? (
      scanResult.match
        ? <div style={{background:'#EAF3EE',border:'0.5px solid #B8DEC4',
            borderRadius:5,padding:'9px 12px',marginBottom:12,
            fontSize:12,color:'#1A5C38',fontWeight:500}}>
            ✓ Document matches database specs — clear to advance
          </div>
        : <div style={{background:'#FDECEA',border:'0.5px solid #F5C0B8',
            borderRadius:5,padding:'9px 12px',marginBottom:12,
            fontSize:12,color:'#C8322A',fontWeight:500}}>
            Discrepancies found — review before advancing
          </div>
    ) : null;
    scanModal = (
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',
        zIndex:400,display:'flex',alignItems:'center',justifyContent:'center'}}
        onClick={function(){setScanItem(null);}}>
        <div style={{background:'#FFFFFF',borderRadius:8,padding:24,
          width:600,maxWidth:'92vw',maxHeight:'88vh',overflowY:'auto',
          border:'0.5px solid #D8D7D2'}}
          onClick={function(e){e.stopPropagation();}}>
          <div style={{display:'flex',justifyContent:'space-between',
            alignItems:'center',marginBottom:4}}>
            <div style={{fontWeight:500,fontSize:14,color:'#111111'}}>
              Verify: {scanItem.item}
            </div>
            <button onClick={function(){setScanItem(null);}}
              style={{background:'none',border:'none',fontSize:16,
                cursor:'pointer',color:'#888880',lineHeight:1}}>
              ✕
            </button>
          </div>
          <div style={{fontSize:11,color:'#888880',marginBottom:16}}>
            {stage} · {scanItem.projectLabel||scanItem.project||''}
          </div>
          <div style={{marginBottom:10}}>
            <label style={{fontSize:9,fontWeight:500,color:'#888880',
              letterSpacing:'0.07em',textTransform:'uppercase',
              display:'block',marginBottom:4}}>
              {scanLabel}
            </label>
            <textarea value={scanText}
              onChange={function(e){setScanText(e.target.value);setScanResult(null);}}
              placeholder="Paste document text here..."
              rows={6}
              style={{width:'100%',padding:'8px 10px',
                border:'0.5px solid #D8D7D2',borderRadius:5,
                fontSize:11,fontFamily:'DM Sans, sans-serif',
                resize:'vertical',boxSizing:'border-box',outline:'none'}} />
          </div>
          <div style={{display:'flex',gap:6,marginBottom:16}}>
            <button
              onClick={function(){runScan(scanItem);}}
              disabled={!scanText.trim()||scanLoading}
              style={{height:32,padding:'0 16px',
                background:scanText.trim()&&!scanLoading?'#111111':'#D8D7D2',
                color:'#FFFFFF',border:'none',borderRadius:5,
                fontSize:11,fontWeight:500,cursor:'pointer',
                fontFamily:'inherit'}}>
              {scanLoading ? 'Verifying…' : 'Verify document'}
            </button>
            <button onClick={function(){setScanText('');setScanResult(null);}}
              style={{height:32,padding:'0 12px',background:'#FFFFFF',
                border:'0.5px solid #D8D7D2',borderRadius:5,
                fontSize:11,cursor:'pointer',color:'#888880',
                fontFamily:'inherit'}}>
              Clear
            </button>
          </div>
          {matchBanner}
          {flags}
          {!scanResult && nextKey && (
            // Verification is advisory, never a hard gate. Pre-2026-07-18
            // the advance button only rendered AFTER a scan, so an item
            // whose paperwork lives offline (or was confirmed by phone)
            // could not be advanced from the Pipeline view at all.
            <div style={{borderTop:'0.5px solid #EFEFEC',paddingTop:12,
              fontSize:11}}>
              <span onClick={function(){advanceTo(scanItem.id,nextKey);}}
                style={{color:'#888880',cursor:'pointer',
                  textDecoration:'underline',
                  textDecorationColor:'#D8D7D2'}}>
                Skip the check — mark {nextKey} →
              </span>
            </div>
          )}
          {scanResult && nextKey && (
            <div style={{borderTop:'0.5px solid #EFEFEC',paddingTop:14,
              display:'flex',gap:8}}>
              <button
                onClick={function(){advanceTo(scanItem.id,nextKey);}}
                style={{height:32,padding:'0 16px',
                  background:scanResult.match?'#1A5C38':'#C8322A',
                  color:'#FFFFFF',border:'none',borderRadius:5,
                  fontSize:11,fontWeight:500,cursor:'pointer',
                  fontFamily:'inherit'}}>
                {scanResult.match
                  ? 'Advance to '+nextKey
                  : 'Advance anyway ('+nextKey+')'}
              </button>
              <button onClick={function(){setScanItem(null);}}
                style={{height:32,padding:'0 12px',background:'#FFFFFF',
                  border:'0.5px solid #D8D7D2',borderRadius:5,
                  fontSize:11,cursor:'pointer',color:'#888880',
                  fontFamily:'inherit'}}>
                Keep at {stage}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── PIPELINE BAR ──────────────────────────────────────────────────────────
  var ZONE_DOT_STAGES = ['Specifying','Quote Received','Client Approved'];

  var pipelineBar = (
    <div style={{display:'flex',overflowX:'auto',borderBottom:'0.5px solid #D8D7D2'}}>
      {STAGES.map(function(s, i) {
        var cnt = stageCounts[s.key] || 0;
        var isActive = fStage === s.key;
        var zone = ZONE_OF[s.key] || 'spec';
        var zoneColor = ZONE_COLOR[zone];
        var showDot = ZONE_DOT_STAGES.indexOf(s.key) >= 0;
        return (
          <div key={s.key}
            onClick={function(){setFZone('All');setFStage(isActive ? 'All' : s.key);}}
            style={{flex:1,minWidth:64,padding:'7px 4px 6px',textAlign:'center',
              cursor:'pointer',position:'relative',
              background:isActive?'#FFFFFF':'transparent',
              borderRight:i<STAGES.length-1?'0.5px solid #EFEFEC':'none'}}>
            <div style={{fontSize:9,color:'#888880',letterSpacing:'0.04em',
              marginBottom:3,whiteSpace:'nowrap',overflow:'hidden',
              textOverflow:'ellipsis',padding:'0 2px'}}>
              {showDot && (
                <span style={{display:'inline-block',width:5,height:5,
                  borderRadius:'50%',background:zoneColor,
                  marginRight:3,verticalAlign:'middle'}} />
              )}
              {s.key}
            </div>
            <div style={{fontSize:15,fontWeight:500,
              color:cnt>0?'#111111':'#D8D7D2',lineHeight:1}}>
              {cnt}
            </div>
            {isActive && (
              <div style={{position:'absolute',bottom:0,left:0,right:0,
                height:2,background:zoneColor}} />
            )}
          </div>
        );
      })}
    </div>
  );

  // ── ATTENTION BANNER ──────────────────────────────────────────────────────
  var attentionBanner = attention > 0 ? (
    <div style={{display:'flex',alignItems:'center',gap:8,padding:'7px 14px',
      borderBottom:'0.5px solid #D8D7D2',background:'#F7F7F5',
      fontSize:11,color:'#555552'}}>
      <div style={{width:7,height:7,borderRadius:'50%',
        background:'#C8322A',flexShrink:0}} />
      <span style={{flex:1}}>
        {attention} item{attention>1?'s':''} waiting for document verification
      </span>
      <span
        onClick={function(){setFStage(SCAN_STAGES[0]);}}
        // Filter shortcut: jump the stage filter to the scan-gated stage
        // so the list shows exactly the items this banner is counting.
        // The target derives from SCAN_STAGES (today: Order Confirmed) —
        // the previous hardcoded 'Quote Received' was a leftover from
        // when THAT stage carried the scan gate, and filtered to an
        // empty list.
        style={{fontSize:11,fontWeight:500,color:'#111111',cursor:'pointer',
          textDecoration:'underline',textDecorationColor:'#D8D7D2'}}>
        Show them →
      </span>
    </div>
  ) : null;

  // ── FILTER BAR ────────────────────────────────────────────────────────────
  var filterBar = (
    <div style={{display:'flex',alignItems:'center',height:34,
      padding:'0 14px',gap:2,borderBottom:'0.5px solid #D8D7D2',
      overflowX:'auto'}}>
      <span style={{fontSize:9,fontWeight:500,color:'#888880',
        letterSpacing:'0.07em',textTransform:'uppercase',
        marginRight:6,whiteSpace:'nowrap'}}>Zone</span>
      {['All','spec','quote','committed'].map(function(z) {
        var label = z==='All'?'All':z==='spec'?'Specifying':
          z==='quote'?'Quoting':'Committed';
        var isOn = z==='All'
          ? (fStage==='All' && fZone==='All')
          : (fZone===z || (fZone==='All' && ZONE_OF[fStage]===z));
        var dot = z!=='All' ? ZONE_COLOR[z] : null;
        return (
          <button key={z}
            onClick={function(){
              if(z==='All'){setFStage('All');setFZone('All');}
              else{setFZone(z);setFStage('All');}
            }}
            style={{height:22,padding:'0 9px',borderRadius:11,
              border:isOn?'0.5px solid #D8D7D2':'0.5px solid transparent',
              fontSize:11,whiteSpace:'nowrap',
              display:'inline-flex',alignItems:'center',gap:5,
              color:isOn?'#111111':'#555552',
              background:isOn?'#FFFFFF':'transparent',
              fontWeight:isOn?500:400,cursor:'pointer',fontFamily:'inherit',
              boxShadow:isOn?'0 0 0 0.5px #D8D7D2':'none'}}>
            {dot && (
              <span style={{width:6,height:6,borderRadius:'50%',
                background:dot,display:'inline-block',flexShrink:0}} />
            )}
            {label}
          </button>
        );
      })}
      <div style={{width:'0.5px',height:14,background:'#D8D7D2',margin:'0 6px'}} />
      <input
        value={fVendor}
        onChange={function(e){setFVendor(e.target.value);}}
        placeholder="Vendor…"
        style={{height:22,border:'0.5px solid #D8D7D2',borderRadius:11,
          padding:'0 10px',fontSize:11,fontFamily:'inherit',
          color:'#111111',background:'#FFFFFF',outline:'none',width:130}} />
    </div>
  );

  // ── TABLE ROWS ────────────────────────────────────────────────────────────
  var tableRows = filtered.map(function(it) {
    var stageInfo = STAGES.find(function(s){return s.key===it.status;}) || STAGES[0];
    var isScanGate = SCAN_STAGES.indexOf(it.status) >= 0;
    var zone = ZONE_OF[it.status] || 'spec';
    var dotColor = ZONE_COLOR[zone];

    var statusLabel = (
      <div>
        <div style={{fontSize:10,fontWeight:500,letterSpacing:'0.04em',
          textTransform:'uppercase',
          color:zone==='spec'?'#7A5C00':
                zone==='quote'?'#D4007A':'#1A5C38'}}>
          {it.status}
        </div>
        {/* The stage's OWN date (2026-07-28 — the old subline printed the
            vendor's lead-time quote under every committed status, so a
            finished item read "INSTALLED · 4-6 WEEKS"). */}
        {(function(){
          var df = STAGE_DATE_FIELDS[it.status];
          var d = df ? fmtShort(it[df]) : '';
          return d ? <div style={{fontSize:9,color:'#888880',marginTop:1}}>{d}</div> : null;
        })()}
      </div>
    );

    var isLockPoint = stageInfo.next === 'Client Approved'
      || stageInfo.action === 'Client Approved';
    var isCommitted = zone === 'committed';

    var btnBg = isLockPoint              ? '#111111'
              : isScanGate&&!isCommitted ? '#D4007A'
              : isCommitted              ? '#1A5C38'
              : '#FFFFFF';
    var btnColor = (isLockPoint||isScanGate&&!isCommitted||isCommitted)
      ? '#FFFFFF' : '#111111';
    var btnBorder = btnBg === '#FFFFFF' ? '0.5px solid #D8D7D2' : btnBg;

    // Physical rows (PO Issued+): the stage Dropdown replaces the
    // one-click-commit button. Picking a stage opens an inline confirm
    // with an editable date (prefilled today) + Save/Cancel — nothing
    // writes until Save (Cimon 2026-07-28: enter the date; and the old
    // instant buttons felt unsafe). Same write as the item page.
    var isPhysical = (STATUSES.indexOf(it.status) >= STATUSES.indexOf('PO Issued')
      || isNonProcured(it.procurementClass))
      && it.status !== 'Cancelled';
    var stageControl = null;
    if (isPhysical) {
      var pending = pendingStage && pendingStage.itemId === it.id ? pendingStage : null;
      stageControl = pending ? (
        <span style={{display:'inline-flex',alignItems:'center',gap:5}}>
          <span style={{fontSize:10,color:'#555552',whiteSpace:'nowrap'}}>{pending.stage}</span>
          {STAGE_DATE_FIELDS[pending.stage] && (
            <input type="date" value={pending.date} data-testid="tracker-stage-date" autoFocus
              onChange={function(e){setPendingStage(Object.assign({},pending,{date:e.target.value}));}}
              onKeyDown={function(e){
                if(e.key==='Enter'){e.preventDefault();applyStageWithDate(it.id, pending.stage, pending.date);}
                if(e.key==='Escape'){setPendingStage(null);}
              }}
              style={{height:24,padding:'0 6px',fontSize:10.5,border:'0.5px solid #D8D7D2',
                borderRadius:4,fontFamily:'inherit'}} />
          )}
          <button data-testid="tracker-stage-save"
            onClick={function(){applyStageWithDate(it.id, pending.stage, pending.date);}}
            style={{height:24,padding:'0 11px',borderRadius:4,border:'1px solid #111111',
              background:'#111111',color:'#FFFFFF',fontSize:10,fontWeight:600,
              cursor:'pointer',fontFamily:'inherit'}}>
            Save
          </button>
          <button onClick={function(){setPendingStage(null);}}
            style={{height:24,padding:'0 7px',borderRadius:4,border:'0.5px solid #D8D7D2',
              background:'#FFFFFF',color:'#888880',fontSize:10,cursor:'pointer',
              fontFamily:'inherit'}}>
            Cancel
          </button>
        </span>
      ) : (
        <span style={{display:'inline-block',width:150,marginBottom:-12}}>
          {/* Trigger shows the CURRENT stage (2026-07-28 — the 'Set
              stage…' placeholder read as a frozen control and hid the
              fact that updates were landing). Items still at PO Issued
              have no physical stage yet, so they keep the prompt. */}
          <Dropdown value={PHYSICAL_STAGE_OPTIONS.some(function(o){return o.value===it.status;})?it.status:''}
            dataField={'tracker-stage-'+it.id} placeholder="Set stage…"
            options={PHYSICAL_STAGE_OPTIONS}
            onChange={function(v){
              if(v===it.status)return; // picking where you already are is a no-op
              setPendingStage({itemId:it.id,stage:v,date:todayIsoStr()});
            }} />
        </span>
      );
    }

    var actionBtn = null;
    if (isScanGate) {
      actionBtn = (
        <button onClick={function(){openScan(it);}}
          style={{height:24,padding:'0 9px',borderRadius:4,
            background:btnBg,color:btnColor,
            border:btnBorder,cursor:'pointer',
            fontSize:10,fontWeight:500,fontFamily:'inherit',
            whiteSpace:'nowrap'}}>
          Verify →
        </button>
      );
    } else if (stageInfo.next) {
      actionBtn = (
        <button onClick={function(){advanceTo(it.id, stageInfo.next);}}
          style={{height:24,padding:'0 9px',borderRadius:4,
            background:btnBg,color:btnColor,
            border:btnBorder,cursor:'pointer',fontSize:10,fontWeight:500,
            fontFamily:'inherit',whiteSpace:'nowrap'}}>
          {stageInfo.action}
        </button>
      );
    }

    var menuOpen = openMenu === it.id;
    var menuBtn = (
      <div style={{position:'relative',display:'inline-block'}}>
        <button
          onClick={function(e){e.stopPropagation();setOpenMenu(menuOpen?null:it.id);}}
          style={{width:24,height:24,borderRadius:4,
            border:'0.5px solid #D8D7D2',background:'#FFFFFF',
            cursor:'pointer',fontSize:11,color:'#888880',
            display:'flex',alignItems:'center',justifyContent:'center',
            fontFamily:'inherit'}}>
          ···
        </button>
        {menuOpen && (
          <div style={{position:'absolute',right:0,top:'100%',zIndex:100,
            background:'#FFFFFF',border:'0.5px solid #D8D7D2',
            borderRadius:6,minWidth:180,padding:4,marginTop:2}} data-testid="tracker-overflow-menu">
            {STAGES.filter(function(s){
                // Only manually-selectable stages. Anything from Client Approved
                // onward is owned by a cascade (approveProposal, cascadePaidItems,
                // PO issuance, etc.) — bypassing it orphans the item.
                return s.key!==it.status && MANUALLY_SELECTABLE_STATUSES.indexOf(s.key)>=0;
              })
              .map(function(s) {
                return (
                  <button key={s.key} data-stage-option={s.key}
                    onClick={function(){advanceTo(it.id,s.key);setOpenMenu(null);}}
                    style={{display:'block',width:'100%',textAlign:'left',
                      padding:'7px 12px',background:'none',border:'none',
                      fontSize:11,cursor:'pointer',color:'#111111',
                      borderRadius:3,fontFamily:'inherit'}}>
                    {s.key}
                  </button>
                );
              })}
            <div style={{borderTop:'0.5px solid #EFEFEC',margin:'4px 0'}} />
            <button
              onClick={function(){
                if(window.confirm('Delete this item?'))
                  p.onDelete && p.onDelete(it.id);
                setOpenMenu(null);
              }}
              style={{display:'block',width:'100%',textAlign:'left',
                padding:'7px 12px',background:'none',border:'none',
                fontSize:11,cursor:'pointer',color:'#C8322A',
                borderRadius:3,fontFamily:'inherit'}}>
              Delete item
            </button>
          </div>
        )}
      </div>
    );

    return (
      <tr key={it.id}
        style={{borderBottom:'0.5px solid #EFEFEC',background:'#FFFFFF'}}>
        <td style={{padding:'9px 10px',maxWidth:200}}>
          <div style={{display:'flex',alignItems:'center',gap:7}}>
            <div style={{width:7,height:7,borderRadius:'50%',
              background:dotColor,flexShrink:0}} />
            <span style={{fontSize:12,fontWeight:500,color:'#111111'}}>
              {it.productName||it.item}
            </span>
            {it.procurementClass==='owner_furnished'&&<span data-testid="procurement-class-badge" data-class="owner_furnished" title="Owner Furnished — client supplies the item" style={{fontSize:9,fontWeight:500,padding:'1px 5px',borderRadius:3,border:'0.5px solid #E0E0DC',background:'#fff',color:'#888880',whiteSpace:'nowrap'}}>OF</span>}
            {it.procurementClass==='by_others'&&<span data-testid="procurement-class-badge" data-class="by_others" title="By Others — installed by another trade" style={{fontSize:9,fontWeight:500,padding:'1px 5px',borderRadius:3,border:'0.5px solid #E0E0DC',background:'#fff',color:'#888880',whiteSpace:'nowrap'}}>BO</span>}
            <ItemDocuments itemId={it.id} compact={true} />
          </div>
        </td>
        <td style={{padding:'9px 10px',fontSize:11,color:'#555552'}}>
          {it.roomNumber?it.roomNumber+' · ':''}{it.roomName||'—'}
        </td>
        <td style={{padding:'9px 10px',fontSize:11,color:'#555552',
          maxWidth:150,overflow:'hidden',textOverflow:'ellipsis',
          whiteSpace:'nowrap'}}>
          {it.vendor||'—'}
        </td>
        <td style={{padding:'9px 10px'}}>{statusLabel}</td>
        {/* EST. SHIP = the vendor's promise (order date + quoted lead
            weeks), kept side by side with ARRIVED so the table shows
            promise vs reality per Cimon 2026-07-28 ("did the lead time
            follow the actual deliverable"). */}
        <td style={{padding:'9px 10px',fontSize:11,color:'#555552',
          fontVariantNumeric:'tabular-nums',whiteSpace:'nowrap'}}>
          {(function(){
            var iso = estimatedShipDate(it);
            return iso ? (fmtShort(iso) || '—') : '—';
          })()}
        </td>
        <td style={{padding:'9px 10px',fontSize:11,color:'#111111',
          fontVariantNumeric:'tabular-nums',whiteSpace:'nowrap'}}>
          {(function(){
            var arr = it.receivedDate || it.deliveredDate;
            return arr ? fmtShort(arr) : '—';
          })()}
        </td>
        <td style={{padding:'9px 10px'}}>
          {(function(){
            var health = threadStateFor(it, moneyBundle, todayIsoStr()).health;
            return <span title={health.summary}
              style={{display:'inline-block',width:9,height:9,borderRadius:'50%',
                background:'#FFFFFF',boxSizing:'border-box',
                border:'1.5px solid '+(RING_HEX[health.ring]||RING_HEX.grey)}} />;
          })()}
        </td>
        <td style={{padding:'9px 10px'}}>
          <div style={{display:'flex',gap:5,alignItems:'center',
            justifyContent:'flex-end'}}>
            {isPhysical ? stageControl : actionBtn}
            {isPhysical && isScanGate && actionBtn}
            {menuBtn}
          </div>
        </td>
      </tr>
    );
  });

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div style={{fontFamily:'DM Sans, sans-serif',fontSize:12,color:'#111111'}}>
      {scanModal}

      <div style={{display:'flex',alignItems:'center',height:44,padding:'0 14px',
        gap:6,borderBottom:'0.5px solid #D8D7D2',background:'#F7F7F5',
        marginBottom:0,flexWrap:'wrap'}}>
        <span style={{fontSize:13,fontWeight:500,marginRight:8}}>Tracker</span>
        {/* Project pill row — click "All" to see every project, click a label to filter */}
        <button onClick={function(){setFProject('');}}
          style={{height:26,padding:'0 12px',borderRadius:13,
            border:fProject===''?'1px solid #111111':'0.5px solid #D8D7D2',
            background:fProject===''?'#111111':'#FFFFFF',
            color:fProject===''?'#FFFFFF':'#444',
            fontSize:11,fontWeight:500,fontFamily:'inherit',cursor:'pointer',whiteSpace:'nowrap'}}>
          All
        </button>
        {(p.projects||[]).map(function(proj){
          var active=fProject===proj.label;
          return (
            <button key={proj.id} onClick={function(){setFProject(proj.label);}}
              style={{height:26,padding:'0 12px',borderRadius:13,
                border:active?'1px solid #111111':'0.5px solid #D8D7D2',
                background:active?'#111111':'#FFFFFF',
                color:active?'#FFFFFF':'#444',
                fontSize:11,fontWeight:500,fontFamily:'inherit',cursor:'pointer',whiteSpace:'nowrap'}}>
              {proj.label}
            </button>
          );
        })}
        <div style={{flex:1}} />
        <div style={{display:'flex',gap:4}}>
          {['pipeline','attention'].map(function(v) {
            return (
              <button key={v} onClick={function(){setView(v);}}
                style={{height:28,padding:'0 11px',borderRadius:5,
                  border:'0.5px solid #D8D7D2',cursor:'pointer',
                  fontSize:11,fontWeight:500,fontFamily:'inherit',
                  background:view===v?'#111111':'#FFFFFF',
                  color:view===v?'#FFFFFF':'#111111'}}>
                {v.charAt(0).toUpperCase()+v.slice(1)}
              </button>
            );
          })}
        </div>
        <div style={{width:'0.5px',height:18,background:'#D8D7D2',margin:'0 4px'}} />
        <button onClick={exportCSV}
          style={{height:28,padding:'0 11px',borderRadius:5,
            border:'0.5px solid #D8D7D2',background:'#FFFFFF',
            cursor:'pointer',fontSize:11,fontWeight:500,fontFamily:'inherit',
            color:'#111111'}}>
          Export CSV
        </button>
      </div>

      {pipelineBar}
      {filterBar}
      {attentionBanner}

      {/* The Thread VIEW was retired 2026-07-28 (Cimon: the mini-threads
          were illegible at row size; the full thread lives on the item
          page where it works). Its useful parts moved into this table:
          the money ring column and the did-this-ship queue below. */}
      {view === 'attention' && (
        <div style={{padding:'20px 14px',fontSize:12,color:'#888880',
          fontStyle:'italic'}}>
          Attention view — coming soon. Will show overdue items,
          upcoming ETAs, and pending approvals.
        </div>
      )}

      {/* Did-this-ship queue (moved from the retired Thread view) —
          items whose shipping needs a human answer: past ETA, vendor
          settled but not shipped, or no lead time to project from. */}
      {view === 'pipeline' && (function(){
        var shipQueue = didThisShipQueue(filtered, moneyBundle, todayIsoStr());
        if (shipQueue.length === 0) return null;
        var REASON_COPY = {
          past_eta: function(q){ return 'ETA passed ' + q.daysPast + 'd ago (' + fmtShort(q.expected) + ')'; },
          vendor_final_paid: function(){ return 'vendor paid in full — has it shipped?'; },
          no_eta: function(){ return 'no lead time — can\u2019t project delivery'; },
        };
        return (
          <div data-testid="ship-queue" style={{borderBottom:'0.5px solid #D8D7D2'}}>
            <button onClick={function(){setShipQueueOpen(!shipQueueOpen);}}
              data-testid="ship-queue-toggle"
              style={{display:'flex',alignItems:'center',gap:8,width:'100%',padding:'7px 14px',
                background:'#FFFFFF',border:'none',cursor:'pointer',fontFamily:'inherit',textAlign:'left'}}>
              <span style={{fontSize:11,fontWeight:500,color:'#8A6D00'}}>
                Did this ship? {shipQueue.length} item{shipQueue.length===1?'':'s'} need{shipQueue.length===1?'s':''} an answer
              </span>
              <span style={{fontSize:9,color:'#888880'}}>{shipQueueOpen?'\u25B2 hide':'\u25BC show'}</span>
            </button>
            {shipQueueOpen && shipQueue.map(function(q){
              var qit = q.item;
              return (
                <div key={qit.id} style={{display:'flex',alignItems:'center',gap:10,
                  padding:'6px 14px 6px 24px',borderTop:'0.5px solid #EFEFEC'}}>
                  <span style={{fontSize:11,color:'#111111',fontWeight:500,minWidth:0,
                    overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:'0 1 auto'}}>
                    {qit.productName||qit.item}
                  </span>
                  <span style={{fontSize:10,color:'#888880',whiteSpace:'nowrap'}}>{qit.vendor}</span>
                  <span style={{fontSize:10,color:q.reason==='past_eta'?'#C8322A':'#8A6D00',flex:1}}>
                    {(REASON_COPY[q.reason]||function(){return '';})(q)}
                  </span>
                  {q.reason !== 'no_eta' && (
                    <button onClick={function(){setPendingStage({itemId:qit.id,stage:'Shipped',date:todayIsoStr()});setShipQueueOpen(false);}}
                      title="Opens the date confirm on this item's row below"
                      style={{height:22,padding:'0 10px',borderRadius:5,fontSize:10,fontWeight:500,
                        fontFamily:'inherit',cursor:'pointer',background:'#FFFFFF',color:'#111111',
                        border:'0.5px solid #D8D7D2',whiteSpace:'nowrap'}}>
                      Mark shipped…
                    </button>
                  )}
                  <button onClick={function(){if(p.onNavigate)p.onNavigate('/items/'+qit.id);}}
                    title={q.reason==='no_eta'?'Open the item and add lead weeks so the ETA can be projected':'Open the item'}
                    style={{height:22,padding:'0 10px',borderRadius:5,fontSize:10,
                      fontFamily:'inherit',cursor:'pointer',background:'#FFFFFF',color:'#555552',
                      border:'0.5px solid #D8D7D2',whiteSpace:'nowrap'}}>
                    {q.reason==='no_eta'?'Add lead time':'Open item'}
                  </button>
                </div>
              );
            })}
          </div>
        );
      })()}

      {view === 'pipeline' && (
        filtered.length === 0
          ? <div style={{textAlign:'center',padding:40,
              color:'#D8D7D2',fontSize:13}}>
              No items match.
            </div>
          : <div>
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead>
                  <tr style={{background:'#F7F7F5',
                    borderBottom:'0.5px solid #D8D7D2'}}>
                    {['Item','Room','Vendor','Status','Est. Ship','Arrived','Money',''].map(
                      function(hd) {
                        return (
                          <th key={hd}
                            style={{padding:'6px 10px',textAlign:'left',
                              fontSize:9,fontWeight:500,color:'#888880',
                              letterSpacing:'0.07em',textTransform:'uppercase',
                              whiteSpace:'nowrap',
                              borderRight:hd===''?'none':
                                '0.5px solid #F7F7F5'}}>
                            {hd}
                          </th>
                        );
                      }
                    )}
                  </tr>
                </thead>
                <tbody>{tableRows}</tbody>
              </table>
              <div style={{display:'flex',gap:20,padding:'9px 14px',
                background:'#F7F7F5',borderTop:'0.5px solid #D8D7D2',
                fontSize:11,color:'#555552'}}>
                <span>
                  <strong style={{color:'#111111',fontWeight:500}}>
                    {filtered.length}
                  </strong> items
                </span>
                {/* Money sums removed 2026-07-28 — the tracker is the
                    physical book (Cimon); dollars live on invoicing +
                    reconciliation surfaces. */}
                {attention > 0 && (
                  <span style={{marginLeft:'auto',color:'#C8322A'}}>
                    {attention} need verification
                  </span>
                )}
              </div>
            </div>
      )}

    </div>
  );
}
